/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { FormEvent, RefObject } from 'react';
import { useState } from 'react';

import { Button, Dialog, TextField, Typography2 } from '@coursera/cds-core';

import { USER_PASSWORD_MAX_LENGTH, USER_PASSWORD_MIN_LENGTH } from 'bundles/user-account/common/constants';

import _t from 'i18n!nls/third-party-auth';

const styles = {
  dialogActions: css`
    display: flex;
    gap: var(--cds-spacing-150);
    justify-content: flex-start;
  `,
};

type Props = {
  open: boolean;
  onDelink: (password: string) => void;
  onClose: () => void;
  returnFocusRef?: RefObject<HTMLElement>;
};

const PasswordDialog = ({ open, onDelink, onClose, returnFocusRef }: Props) => {
  const [password, setPassword] = useState('');

  if (!open) {
    return null;
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onDelink(password);
    setPassword('');
    onClose();
  };

  const handleCancel = () => {
    setPassword('');
    onClose();
  };

  return (
    <Dialog open onClose={handleCancel} returnFocusRef={returnFocusRef}>
      <Dialog.HeadingGroup>{_t('Enter your password')}</Dialog.HeadingGroup>
      <form onSubmit={handleSubmit}>
        <Dialog.Content>
          <Typography2 variant="bodyPrimary" component="p">
            {_t('Please set a password for future logins to your Coursera account.')}
          </Typography2>
          <TextField
            id="account-link-password-input"
            type="password"
            label={_t('Password')}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            inputProps={{
              minLength: USER_PASSWORD_MIN_LENGTH,
              maxLength: USER_PASSWORD_MAX_LENGTH,
              required: true,
            }}
            fullWidth
          />
        </Dialog.Content>
        <Dialog.Actions>
          <div css={styles.dialogActions}>
            <Button type="submit" variant="primary" disabled={password.length === 0}>
              {_t('Unlink')}
            </Button>
            <Button type="button" variant="ghost" onClick={handleCancel}>
              {_t('Cancel')}
            </Button>
          </div>
        </Dialog.Actions>
      </form>
    </Dialog>
  );
};

export default PasswordDialog;
